/**
 * Notification Repository
 *
 * Manages system notifications for workflow events, escalations, and user actions
 * FERP SAFETY: Critical for user awareness of workflow state changes
 */

import { BaseRepository } from './BaseRepository';
import { Notification } from '../types/WorkflowEntities';

class NotificationRepository extends BaseRepository<Notification> {
    protected entityName = 'notification';
    protected graphQLType = 'Notification';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Notification | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Notification',
            criticalityLevel: 'MEDIUM', // Notifications are important but not life-critical
            metadata: { id, notificationRepository: true }
        });

        const query = `
            query GetNotificationByTenantAndId($id: String!, $tenant: String!) {
                notification(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ notification: Notification | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 1 * 60 * 1000 // 1 minute cache for notifications
            });

            const notification = result.notification || null;

            // ✅ FERP SAFETY: Log successful operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Notification',
                    entityId: id,
                    found: !!notification,
                    notificationType: notification?.type,
                    read: notification?.read,
                    timestamp: Date.now()
                });
            }

            return notification;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Notification',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(notificationId: string): Promise<Notification | null> {
        return this.findByTenantAndId(this.tenant, notificationId);
    }

    // Notification-specific methods

    /**
     * Get all notifications for current user
     * FERP SAFETY: User-scoped data isolation
     */
    async getNotifications(options?: {
        read?: boolean;
        type?: Notification['type'];
        severity?: Notification['severity'];
        limit?: number;
        offset?: number;
    }): Promise<Notification[]> {
        await this.validateRepositorySafety({
            operation: 'GET_NOTIFICATIONS',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Notification',
            criticalityLevel: 'MEDIUM',
            metadata: { options }
        });

        const query = `
            query GetUserNotifications(
                $tenant: String!,
                $read: Boolean,
                $type: String,
                $severity: String,
                $limit: Int,
                $offset: Int
            ) {
                userNotifications(
                    tenant: $tenant,
                    read: $read,
                    type: $type,
                    severity: $severity,
                    limit: $limit,
                    offset: $offset
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            tenant: this.tenant,
            read: options?.read,
            type: options?.type,
            severity: options?.severity,
            limit: options?.limit || 50,
            offset: options?.offset || 0
        });

        return result.userNotifications || [];
    }

    /**
     * Get unread notification count
     * FERP SAFETY: Real-time awareness of pending items
     */
    async getUnreadCount(): Promise<number> {
        const query = `
            query GetUnreadNotificationCount($tenant: String!) {
                unreadNotificationCount(tenant: $tenant)
            }
        `;

        const result = await this.executeQuery(query, {
            tenant: this.tenant
        });

        return result.unreadNotificationCount || 0;
    }

    /**
     * Mark notification as read
     * FERP SAFETY: Track user acknowledgment
     */
    async markAsRead(notificationId: string): Promise<Notification> {
        await this.validateRepositorySafety({
            operation: 'MARK_AS_READ',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Notification',
            criticalityLevel: 'MEDIUM',
            metadata: { notificationId }
        });

        const mutation = `
            mutation MarkNotificationAsRead($notificationId: ID!, $tenant: String!) {
                markNotificationAsRead(notificationId: $notificationId, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            notificationId,
            tenant: this.tenant
        });

        return result.markNotificationAsRead;
    }

    /**
     * Mark all notifications as read
     * FERP SAFETY: Bulk operation with audit trail
     */
    async markAllAsRead(options?: {
        type?: Notification['type'];
        severity?: Notification['severity'];
    }): Promise<{ count: number }> {
        await this.validateRepositorySafety({
            operation: 'MARK_ALL_AS_READ',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Notification',
            criticalityLevel: 'MEDIUM',
            metadata: { options }
        });

        const mutation = `
            mutation MarkAllNotificationsAsRead(
                $tenant: String!,
                $type: String,
                $severity: String
            ) {
                markAllNotificationsAsRead(
                    tenant: $tenant,
                    type: $type,
                    severity: $severity
                ) {
                    count
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            tenant: this.tenant,
            type: options?.type,
            severity: options?.severity
        });

        return result.markAllNotificationsAsRead || { count: 0 };
    }

    /**
     * Dismiss notification
     * FERP SAFETY: User can hide notifications while preserving audit trail
     */
    async deleteNotification(notificationId: string): Promise<{ success: boolean }> {
        await this.validateRepositorySafety({
            operation: 'DELETE_NOTIFICATION',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Notification',
            criticalityLevel: 'MEDIUM',
            metadata: { notificationId }
        });

        const mutation = `
            mutation DismissNotification($notificationId: ID!, $tenant: String!) {
                dismissNotification(notificationId: $notificationId, tenant: $tenant) {
                    success
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            notificationId,
            tenant: this.tenant
        });

        return result.dismissNotification || { success: false };
    }

    /**
     * Get notifications for specific workflow
     * FERP SAFETY: Context-aware notification display
     */
    async getWorkflowNotifications(workflowId: string, options?: {
        read?: boolean;
        limit?: number;
    }): Promise<Notification[]> {
        const query = `
            query GetWorkflowNotifications(
                $workflowId: ID!,
                $tenant: String!,
                $read: Boolean,
                $limit: Int
            ) {
                workflowNotifications(
                    workflowId: $workflowId,
                    tenant: $tenant,
                    read: $read,
                    limit: $limit
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            workflowId,
            tenant: this.tenant,
            read: options?.read,
            limit: options?.limit || 50
        });

        return result.workflowNotifications || [];
    }

    /**
     * Get notifications by type
     * FERP SAFETY: Filter critical notifications
     */
    async getNotificationsByType(type: Notification['type'], options?: {
        read?: boolean;
        limit?: number;
    }): Promise<Notification[]> {
        const query = `
            query GetNotificationsByType(
                $type: String!,
                $tenant: String!,
                $read: Boolean,
                $limit: Int
            ) {
                notificationsByType(
                    type: $type,
                    tenant: $tenant,
                    read: $read,
                    limit: $limit
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            type,
            tenant: this.tenant,
            read: options?.read,
            limit: options?.limit || 50
        });

        return result.notificationsByType || [];
    }

    /**
     * Get notification statistics
     * FERP SAFETY: Overview of notification distribution
     */
    async getNotificationStats(): Promise<{
        total: number;
        unread: number;
        byType: Record<Notification['type'], number>;
        bySeverity: Record<Notification['severity'], number>;
    }> {
        const query = `
            query GetNotificationStatistics($tenant: String!) {
                notificationStatistics(tenant: $tenant) {
                    total
                    unread
                    byType
                    bySeverity
                }
            }
        `;

        const result = await this.executeQuery(query, {
            tenant: this.tenant
        });

        return result.notificationStatistics || {
            total: 0,
            unread: 0,
            byType: {},
            bySeverity: {}
        };
    }

    protected getFieldSelection(): string {
        return `
            id
            userId
            type
            title
            message
            severity
            read
            dismissed
            workflowId
            instanceId
            taskId
            data
            actionUrl
            actionLabel
            expiresAt
            createdAt
            readAt
            dismissedAt
        `;
    }
}

export const notificationRepository = new NotificationRepository();
export { NotificationRepository };
